import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { publicApi } from "@/lib/api";
import { brl } from "@/lib/format";
import { Check, ArrowRight } from "lucide-react";
import { PLAN_COPY, COMPARE_ROWS } from "@/lib/marketing";
import { trackEvent } from "./Layout";

export const usePlans = () => {
  const [plans, setPlans] = useState([]);
  useEffect(() => { publicApi.get("/public/plans").then((r) => setPlans(r.data)).catch(() => {}); }, []);
  return plans;
};

export const PlanCards = ({ compact = false }) => {
  const plans = usePlans();
  return (
    <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-5">
      {plans.map((p) => {
        const featured = p.id === "profissional";
        const copy = PLAN_COPY[p.id] || { tagline: p.description, items: [] };
        return (
          <div key={p.id} data-testid={`plan-card-${p.id}`} className={`relative rounded-2xl p-6 border flex flex-col ${featured ? "border-orange-600 bg-orange-50/40 shadow-lg" : "border-stone-200 bg-white"}`}>
            {featured && <div className="absolute -top-3 left-6 rounded-full bg-orange-600 text-white text-xs font-bold px-3 py-1">Recomendado</div>}
            <div className="text-sm text-stone-500 font-semibold">{copy.tagline}</div>
            <h3 className="font-display text-2xl font-extrabold text-stone-900 mt-1">{p.name}</h3>
            <div className="mt-4 flex items-baseline gap-1">
              <span className="font-mono-num text-4xl font-extrabold text-stone-900">{p.price === 0 ? "R$ 0" : brl(p.price)}</span>
              {p.price > 0 && <span className="text-stone-500 text-sm">/mês</span>}
            </div>
            {!compact && <ul className="mt-6 space-y-2.5 text-sm text-stone-700 flex-1">
              {copy.items.map((it) => <li key={it} className="flex gap-2"><Check className="h-4 w-4 text-emerald-600 shrink-0 mt-0.5" /> {it}</li>)}
            </ul>}
            <Link to={`/comecar?plan=${p.id}`} onClick={() => trackEvent("landing_cta_clicked", { cta: `plan_${p.id}` })} className="mt-6">
              <Button className={`w-full ${featured ? "bg-orange-600 hover:bg-orange-700" : ""}`} variant={featured ? "default" : "outline"} data-testid={`plan-cta-${p.id}`}>
                Começar agora <ArrowRight className="h-4 w-4 ml-1" />
              </Button>
            </Link>
          </div>
        );
      })}
    </div>
  );
};

export const PlanCompare = () => (
  <div className="overflow-x-auto rounded-2xl border border-stone-200 bg-white" data-testid="plan-compare">
    <table className="w-full text-sm min-w-[640px]">
      <thead><tr className="bg-stone-50 text-left">
        <th className="p-4 font-semibold">Recurso</th>{["Grátis", "Essencial", "Profissional", "Master"].map((h) => <th key={h} className={`p-4 font-semibold text-center ${h === "Profissional" ? "text-orange-700" : ""}`}>{h}</th>)}
      </tr></thead>
      <tbody>{COMPARE_ROWS.map((r) => (
        <tr key={r[0]} className="border-t border-stone-100">
          <td className="p-4 text-stone-700">{r[0]}</td>{r.slice(1).map((c, i) => <td key={i} className={`p-4 text-center ${c === "—" ? "text-stone-300" : "text-stone-900 font-medium"}`}>{c}</td>)}
        </tr>))}</tbody>
    </table>
  </div>
);

export const DashboardMockup = () => (
  <div className="rounded-2xl border border-stone-200 bg-white shadow-2xl shadow-stone-300/40 overflow-hidden" data-testid="hero-mockup" aria-hidden="true">
    <div className="flex items-center gap-1.5 px-4 py-2.5 border-b border-stone-100 bg-stone-50">
      <span className="h-2.5 w-2.5 rounded-full bg-rose-400" /><span className="h-2.5 w-2.5 rounded-full bg-amber-400" /><span className="h-2.5 w-2.5 rounded-full bg-emerald-400" />
      <span className="ml-3 text-[11px] text-stone-400 font-mono">app.portalgestao — Dashboard</span>
    </div>
    <div className="grid grid-cols-3 gap-3 p-4">
      {[["Vendas hoje", "R$ 2.418,00", "text-stone-900"], ["Pedidos", "64", "text-stone-900"], ["Margem est.", "31%", "text-emerald-600"]].map(([l, v, c]) => (
        <div key={l} className="rounded-xl border border-stone-100 p-3"><div className="text-[10px] uppercase text-stone-400 font-semibold">{l}</div><div className={`font-mono-num text-lg font-extrabold ${c}`}>{v}</div></div>
      ))}
    </div>
    <div className="px-4 pb-4 grid grid-cols-5 gap-3">
      <div className="col-span-3 rounded-xl border border-stone-100 p-3">
        <div className="text-[10px] uppercase text-stone-400 font-semibold mb-2">Vendas — 7 dias</div>
        <div className="flex items-end gap-1.5 h-20">{[35, 50, 42, 68, 60, 85, 74].map((h, i) => <div key={i} className="flex-1 rounded-t bg-orange-500/80" style={{ height: `${h}%` }} />)}</div>
      </div>
      <div className="col-span-2 rounded-xl border border-stone-100 p-3 space-y-2">
        <div className="text-[10px] uppercase text-stone-400 font-semibold">Alertas</div>
        <div className="rounded-md bg-amber-50 text-amber-800 text-[11px] px-2 py-1.5">3 insumos abaixo do mínimo</div>
        <div className="rounded-md bg-emerald-50 text-emerald-800 text-[11px] px-2 py-1.5">CMV do X-Bacon: 34%</div>
        <div className="rounded-md bg-stone-50 text-stone-700 text-[11px] px-2 py-1.5">12 clientes recorrentes</div>
      </div>
    </div>
  </div>
);
